import React from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import ModalComponent from "./Modal";

const BulkLoadResultsModal = ({
  open,
  onClose,
  result,
  title = "Resultado de carga",
  labels = {
    total: "Total registros",
    created: "Creados",
    updated: "Actualizados",
    failed: "Errores",
    skipped: "Sin cambios",
  },
  keys = {
    total: "total",
    created: "created",
    updated: "updated",
    failed: "failed",
    errorDetails: "error_details",
    skipped: "skipped",
  },
  parseErrorMessage,
  renderErrorItem,
  onDownloadErrors,
  width = "600px",
}) => {
  const get = (obj, key, fallback = 0) => (obj && key && obj[key] != null ? obj[key] : fallback);

  const total = get(result, keys.total, 0);
  const created = get(result, keys.created, 0);
  const updated = keys.updated != null ? get(result, keys.updated, null) : null;
  const failed = get(result, keys.failed, 0);
  const skipped = keys.skipped != null ? get(result, keys.skipped, null) : null;
  const errorDetails = (result && result[keys.errorDetails]) || [];

  const hasErrors = Number(failed) > 0;
  const shouldShow = (val) => val != null && Number(val) > 0;

  const defaultParse = (val) => {
    if (!val) return "Ocurrió un error inesperado.";
    if (typeof parseErrorMessage === "function") return parseErrorMessage(val);
    if (typeof val === "string") return val;
    if (Array.isArray(val)) {
      return val
        .map((e) => {
          if (typeof e === "string") return e;
          if (e?.message) return e.message;
          return JSON.stringify(e);
        })
        .join("; ");
    }
    // Si es un objeto pero no es un array
    if (typeof val === "object") {
      if (val?.message) return val.message;
      if (val?.error) return val.error;
      return JSON.stringify(val);
    }
    return val?.message ?? JSON.stringify(val);
  };

  const defaultRenderErrorItem = (err, idx) => {
    const line = [];

    if (err?.row == null) {
      // Filtrar solo las claves que no sean "errors"
      const filteredEntries = Object.entries(err).filter(([key]) => key !== "errors");
      // Convertir las entradas restantes en el formato deseado
      const keyValueArray = filteredEntries.map(([key, value]) => `${key}: ${value}`);
      // Para mostrar otros encabezados que no sean fila o email
      line.push(keyValueArray.join(", "));
    } else line.push(`Fila ${err.row}`);

    if (err?.question_code) line.push(`Código pregunta: ${err.question_code}`);

    if (err?.email) line.push(`${err.email}`);

    const header = line.join(" - ");

    let body = null;

    if (Array.isArray(err?.errors)) {
      body = (
        <>
          {err.errors.map((e, i) => {
            const code = e?.code ? ` (código: ${e.code})` : "";
            return (
              <Box key={i} component="span" display="block">
                • {defaultParse(e)}
                <span style={{ color: "#6c6c6c" }}>{code}</span>
              </Box>
            );
          })}
        </>
      );
    } else if (typeof err?.errors === "string") {
      body = <>{defaultParse(err.errors)}</>;
    } else if (err?.message || err?.error) {
      body = (
        <>
          {err?.error && (
            <Box component="span" display="block">
              • {err.error}
            </Box>
          )}
          {err?.message && (
            <Box component="span" display="block">
              • {err.message}
            </Box>
          )}
        </>
      );
    } else {
      body = <>{defaultParse(err)}</>;
    }

    return (
      <Typography
        key={idx}
        variant="body2"
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: idx % 2 === 0 ? "action.hover" : "transparent",
          mb: 0.5,
        }}
      >
        {header && (
          <>
            <strong>{header}</strong>
            <br />
          </>
        )}
        {body}
      </Typography>
    );
  };

  return (
    <ModalComponent open={open} handleClose={onClose} width={width}>
      <Box sx={{ p: 3 }}>
        {/* Encabezado */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          {hasErrors ? (
            <ErrorOutline color="error" sx={{ fontSize: 40, mb: 1 }} />
          ) : (
            <CheckCircleOutline color="success" sx={{ fontSize: 40, mb: 1 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>

        {/* Totales */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            "& > div": {
              textAlign: "center",
              flex: 1,
              p: 2,
              borderRadius: 1,
              mx: 1,
              bgcolor: "background.paper",
            },
          }}
        >
          <div>
            <Typography variant="body2" color="textSecondary">
              {labels.total}
            </Typography>
            <Typography variant="h4">{total}</Typography>
          </div>

          {shouldShow(created) && (
            <div>
              <Typography variant="body2" color="success.main">
                {labels.created}
              </Typography>
              <Typography variant="h4" color="success.main">
                {created}
              </Typography>
            </div>
          )}

          {updated != null && shouldShow(updated) && (
            <div>
              <Typography variant="body2" color="info.main">
                {labels.updated}
              </Typography>
              <Typography variant="h4" color="info.main">
                {updated}
              </Typography>
            </div>
          )}

          {shouldShow(failed) && (
            <div>
              <Typography variant="body2" color="error.main">
                {labels.failed}
              </Typography>
              <Typography variant="h4" color="error.main">
                {failed}
              </Typography>
            </div>
          )}

          {skipped != null && shouldShow(skipped) && (
            <div>
              <Typography variant="body2" color="warning.main">
                {labels.skipped ?? "Sin cambios"}
              </Typography>
              <Typography variant="h4" color="warning.main">
                {skipped}
              </Typography>
            </div>
          )}
        </Box>

        {/* Detalle de errores */}
        {hasErrors && Array.isArray(errorDetails) && errorDetails.length > 0 && (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 240,
              overflowY: "auto",
              p: 1,
            }}
          >
            <Typography variant="subtitle2" color="warning" sx={{ mb: 1, px: 1 }}>
              Reporte:
            </Typography>

            {renderErrorItem ? errorDetails.map(renderErrorItem) : errorDetails.map(defaultRenderErrorItem)}
          </Box>
        )}

        {/* Acciones */}
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button onClick={onClose} variant="outlined" size="small">
            Cerrar
          </Button>

          {hasErrors && typeof onDownloadErrors === "function" && (
            <Button onClick={() => onDownloadErrors(errorDetails)} variant="contained" size="small">
              Descargar reporte en Excel
            </Button>
          )}
        </Box>
      </Box>
    </ModalComponent>
  );
};

export default BulkLoadResultsModal;
